'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * A slow constellation of marketplace nodes for the Mirakl network section.
 *
 * Rules this component holds itself to, because the page is a static export
 * that has to keep its search performance:
 *  - three.js is imported only after the section scrolls into view, so it is
 *    never part of the first load and cannot touch LCP;
 *  - nothing renders at all when the visitor asks for reduced motion;
 *  - the loop stops the moment the canvas leaves the viewport or the tab
 *    goes to the background;
 *  - device pixel ratio is capped, so a retina phone does not render four
 *    times the pixels for an ambient background.
 * Everything it draws is decoration - the marketplace names next to it are
 * real text in the DOM, which is what crawlers read.
 */

const NODE_COUNT = 34;
const LINK_DISTANCE = 2.6;

/** A soft round dot, so the nodes are not drawn as squares. */
function createDotTexture(THREE: typeof import('three')) {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.45, 'rgba(255,255,255,0.85)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

export function MiraklNetworkCanvas() {
  const hostRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setActive(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(host);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!active || !host) {
      return;
    }

    let disposed = false;
    let cleanup = () => {};

    void (async () => {
      const THREE = await import('three');
      if (disposed || !host) {
        return;
      }

      const width = host.clientWidth;
      const height = host.clientHeight;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(52, width / height, 0.1, 100);
      camera.position.z = 11.5;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
      renderer.setSize(width, height);
      host.appendChild(renderer.domElement);

      // Nodes on a sphere shell, so the cluster reads as a network rather than
      // a flat scatter. Seeded by index - no randomness that changes per load.
      const positions = new Float32Array(NODE_COUNT * 3);
      const golden = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < NODE_COUNT; i += 1) {
        const y = 1 - (i / (NODE_COUNT - 1)) * 2;
        const radius = Math.sqrt(Math.max(0, 1 - y * y));
        const theta = golden * i;
        const scale = 3.4 + ((i * 37) % 11) * 0.06;
        positions[i * 3] = Math.cos(theta) * radius * scale;
        positions[i * 3 + 1] = y * scale * 0.72;
        positions[i * 3 + 2] = Math.sin(theta) * radius * scale;
      }

      const nodeGeometry = new THREE.BufferGeometry();
      nodeGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const nodes = new THREE.Points(
        nodeGeometry,
        new THREE.PointsMaterial({
          color: 0xf4730e,
          size: 0.1,
          transparent: true,
          opacity: 0.55,
          // Without a mask, points draw as hard squares and read as artefacts.
          map: createDotTexture(THREE),
          alphaTest: 0.02,
          depthWrite: false,
        }),
      );

      const linkPoints: number[] = [];
      for (let a = 0; a < NODE_COUNT; a += 1) {
        for (let b = a + 1; b < NODE_COUNT; b += 1) {
          const dx = positions[a * 3] - positions[b * 3];
          const dy = positions[a * 3 + 1] - positions[b * 3 + 1];
          const dz = positions[a * 3 + 2] - positions[b * 3 + 2];
          if (Math.sqrt(dx * dx + dy * dy + dz * dz) < LINK_DISTANCE) {
            linkPoints.push(
              positions[a * 3], positions[a * 3 + 1], positions[a * 3 + 2],
              positions[b * 3], positions[b * 3 + 1], positions[b * 3 + 2],
            );
          }
        }
      }
      const linkGeometry = new THREE.BufferGeometry();
      linkGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linkPoints, 3));
      const links = new THREE.LineSegments(
        linkGeometry,
        new THREE.LineBasicMaterial({ color: 0x1f4bee, transparent: true, opacity: 0.14 }),
      );

      const group = new THREE.Group();
      group.add(nodes);
      group.add(links);
      scene.add(group);

      let frame = 0;
      let running = true;

      const renderFrame = () => {
        if (!running) {
          return;
        }
        group.rotation.y += 0.0016;
        group.rotation.x = Math.sin(group.rotation.y * 0.4) * 0.12;
        renderer.render(scene, camera);
        frame = requestAnimationFrame(renderFrame);
      };

      const start = () => {
        if (!running) {
          running = true;
          frame = requestAnimationFrame(renderFrame);
        }
      };
      const stop = () => {
        running = false;
        cancelAnimationFrame(frame);
      };

      const visibility = new IntersectionObserver(
        (entries) => (entries.some((entry) => entry.isIntersecting) ? start() : stop()),
        { threshold: 0 },
      );
      visibility.observe(host);

      const onTabChange = () => (document.hidden ? stop() : start());
      document.addEventListener('visibilitychange', onTabChange);

      const onResize = () => {
        const w = host.clientWidth;
        const h = host.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener('resize', onResize);

      frame = requestAnimationFrame(renderFrame);

      cleanup = () => {
        stop();
        visibility.disconnect();
        document.removeEventListener('visibilitychange', onTabChange);
        window.removeEventListener('resize', onResize);
        nodeGeometry.dispose();
        linkGeometry.dispose();
        nodes.material.map?.dispose();
        nodes.material.dispose();
        links.material.dispose();
        renderer.dispose();
        renderer.domElement.remove();
      };
    })();

    return () => {
      disposed = true;
      cleanup();
    };
  }, [active]);

  return <div ref={hostRef} className="absolute inset-0" aria-hidden="true" />;
}
